/* global gdAlertify */
import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney } from "../utils/utils";
import "./ReajusteStyles.css";

const ReajusteAuxiliar = () => {
    const [resultados, setResultados] = useState([]);
    const [modificaciones, setModificaciones] = useState({});
    const [busqueda, setBusqueda] = useState("");
    const [loading, setLoading] = useState(false);
    const [expandidos, setExpandidos] = useState({});

    // Fecha desde cuando aplica el reajuste (por defecto, primer día del mes siguiente)
    const getDefaultFechaReajuste = () => {
        const hoy = new Date();
        const primerDiaMesSiguiente = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
        return primerDiaMesSiguiente.toISOString().split('T')[0];
    };
    const [fechaReajuste, setFechaReajuste] = useState(getDefaultFechaReajuste());

    const buscarReajuste = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/auxiliar/reajuste");
            setResultados(response.data.data);
        } catch (error) {
            console.error("Error al buscar reajustes: ", error);
            gdAlertify.alertError("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        buscarReajuste();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Construir array con un registro por paciente modificado
        const datosModificados = Object.entries(modificaciones).map(([rutPaciente, cambios]) => {
            // Obtener el primer acuerdo del paciente para sacar el idPaciente
            const primerAcuerdo = resultados.find(r => {
                const key = r.rut_paciente || r.nombre_paciente || 'sin-paciente';
                return key === rutPaciente;
            });

            // Buscar el ID del paciente en diferentes campos posibles
            const idPaciente = primerAcuerdo?.idPaciente ||
                primerAcuerdo?.paciente_id ||
                primerAcuerdo?.id_paciente ||
                primerAcuerdo?.pacienteId ||
                null;

            console.log("🔍 Debug - Paciente:", rutPaciente);
            console.log("📋 Primer acuerdo:", primerAcuerdo);
            console.log("🆔 ID Paciente encontrado:", idPaciente);

            return {
                idPaciente: idPaciente,
                valorTurno: cambios.valorTurno,
                fechaReajuste: fechaReajuste
            };
        });

        // Mostrar todos los datos antes de filtrar
        console.log("📦 Datos antes de filtrar:", datosModificados);

        const datosFiltrados = datosModificados.filter(d => d.idPaciente !== null);

        console.log("✅ Datos después de filtrar:", datosFiltrados);

        if (datosFiltrados.length === 0) {
            gdAlertify.notify("No hay cambios para guardar", "warning");
            return;
        }

        // Validar que los valores sean positivos
        const valoresInvalidos = datosFiltrados.filter(d => d.valorTurno <= 0);
        if (valoresInvalidos.length > 0) {
            gdAlertify.notify("Los valores deben ser mayores a 0", "error");
            return;
        }

        const cantidadPacientes = datosFiltrados.length;

        gdAlertify.confirm({
            title: "Confirmar Reajuste",
            message: `¿Está seguro de guardar reajustes para ${cantidadPacientes} paciente(s) con fecha de aplicación ${formatDate(fechaReajuste)}?`,
            onConfirm: async () => {
                try {
                    setLoading(true);

                    console.log("📤 Datos enviados al backend:", JSON.stringify({ datos: datosFiltrados }, null, 2));

                    await axiosInstance.post("/auxiliar/reajuste/guardar", { datos: datosFiltrados });
                    gdAlertify.alertSuccess(`Reajustes guardados con éxito para ${cantidadPacientes} paciente(s)`);
                    buscarReajuste();
                    setModificaciones({});
                } catch (error) {
                    console.error("Error al guardar reajustes: ", error);
                    gdAlertify.alertError("Error al guardar los reajustes");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleInputChange = (rutPaciente, valor) => {
        if (valor === "" || valor === null) {
            setModificaciones(prev => {
                const newMod = { ...prev };
                delete newMod[rutPaciente];
                return newMod;
            });
        } else {
            setModificaciones(prev => ({
                ...prev,
                [rutPaciente]: {
                    valorTurno: parseFloat(valor),
                },
            }));
        }
    };

    const toggleExpand = (rutPaciente) => {
        setExpandidos(prev => ({
            ...prev,
            [rutPaciente]: !prev[rutPaciente]
        }));
    };

    const getMesesDesdeReajuste = (fechaReajuste) => {
        if (!fechaReajuste) return null;
        const fecha = new Date(fechaReajuste);
        const hoy = new Date();
        const meses = (hoy.getFullYear() - fecha.getFullYear()) * 12 + (hoy.getMonth() - fecha.getMonth());
        return meses;
    };

    const getBadgeClass = (meses) => {
        if (meses === null) return "badge-neutral";
        if (meses >= 6) return "badge-danger";
        if (meses >= 4) return "badge-warning";
        return "badge-success";
    };

    const getBadgeText = (meses) => {
        if (meses === null) return "Sin registro";
        if (meses === 0) return "Este mes";
        if (meses === 1) return "Hace 1 mes";
        return `Hace ${meses} meses`;
    };

    // Agrupar por paciente
    const pacientesAgrupados = resultados.reduce((acc, row) => {
        const key = row.rut_paciente || row.nombre_paciente || 'sin-paciente';
        if (!acc[key]) {
            acc[key] = {
                nombrePaciente: row.nombre_paciente || 'Sin nombre',
                rutPaciente: row.rut_paciente || '',
                auxiliares: []
            };
        }
        acc[key].auxiliares.push(row);
        return acc;
    }, {});

    const datosFiltrados = Object.entries(pacientesAgrupados).filter(([key, data]) => {
        const searchLower = busqueda.toLowerCase();
        const nombrePaciente = data.nombrePaciente || '';
        const rutPaciente = data.rutPaciente || '';
        const tieneAuxiliarBuscado = data.auxiliares.some(aux => {
            const nombre = aux.nombre || '';
            const rut = aux.rut || '';
            return nombre.toLowerCase().includes(searchLower) || rut.includes(busqueda);
        });

        return nombrePaciente.toLowerCase().includes(searchLower) ||
            rutPaciente.includes(busqueda) ||
            tieneAuxiliarBuscado;
    });

    const cantidadModificaciones = Object.keys(modificaciones).length;

    return (
        <div className="reajuste-container">
            {/* Header */}
            <div className="reajuste-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>💰 Reajuste de Auxiliares por Paciente</h2>
                        <p>Gestiona los valores de turno de auxiliares agrupados por paciente</p>
                    </div>
                    {cantidadModificaciones > 0 && (
                        <div className="modificaciones-badge pulse">
                            <span className="badge-number">{cantidadModificaciones}</span>
                            <span className="badge-text">paciente{cantidadModificaciones > 1 ? 's' : ''} modificado{cantidadModificaciones > 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Controles */}
            <div className="reajuste-controls">
                <div className="controls-row">
                    <div className="search-box">
                        <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Buscar por paciente, auxiliar o RUT..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                        {busqueda && (
                            <button className="clear-search" onClick={() => setBusqueda("")}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="fecha-reajuste-box">
                        <label className="fecha-label">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            Fecha de aplicación:
                        </label>
                        <input
                            type="date"
                            className="fecha-input"
                            value={fechaReajuste}
                            onChange={(e) => setFechaReajuste(e.target.value)}
                        />
                    </div>

                    <button
                        className={`btn-save ${cantidadModificaciones > 0 ? 'active' : ''}`}
                        onClick={handleSubmit}
                        disabled={cantidadModificaciones === 0 || loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>

                <div className="results-info">
                    <span className="results-count">
                        {datosFiltrados.length} paciente{datosFiltrados.length !== 1 ? 's' : ''} encontrado{datosFiltrados.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {/* Tabla agrupada */}
            <div className="reajuste-table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando datos...</p>
                    </div>
                ) : datosFiltrados.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h3>No se encontraron pacientes</h3>
                        <p>Intenta ajustar los filtros de búsqueda</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="reajuste-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}></th>
                                    <th>Paciente</th>
                                    <th>Auxiliares</th>
                                    <th>Último Reajuste</th>
                                    <th>Valor Actual</th>
                                    <th>Nuevo Valor</th>
                                    <th>Diferencia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {datosFiltrados.map(([key, pacienteData]) => {
                                    const primerAuxiliar = pacienteData.auxiliares[0];
                                    const meses = getMesesDesdeReajuste(primerAuxiliar.ultimoReajuste);
                                    const valorModificado = modificaciones[key]?.valorTurno;
                                    const valorActual = primerAuxiliar.valorTurno;
                                    const diferencia = valorModificado ? valorModificado - valorActual : 0;
                                    const porcentaje = valorActual > 0 ? ((diferencia / valorActual) * 100).toFixed(1) : 0;
                                    const hasChange = modificaciones[key] !== undefined;
                                    const isExpanded = expandidos[key];

                                    return (
                                        <React.Fragment key={key}>
                                            <tr className={hasChange ? 'row-modified' : ''}>
                                                <td>
                                                    <button
                                                        className="expand-btn"
                                                        onClick={() => toggleExpand(key)}
                                                        title={isExpanded ? "Ocultar auxiliares" : "Ver auxiliares"}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                                            <polyline points="9 18 15 12 9 6" />
                                                        </svg>
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="patient-info-simple">
                                                        <div className="patient-name">{pacienteData.nombrePaciente}</div>
                                                        {pacienteData.rutPaciente && (
                                                            <div className="patient-rut">{pacienteData.rutPaciente}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="auxiliares-count">
                                                        {pacienteData.auxiliares.length} auxiliar{pacienteData.auxiliares.length > 1 ? 'es' : ''}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="reajuste-date">
                                                        <span className={`time-badge ${getBadgeClass(meses)}`}>
                                                            {getBadgeText(meses)}
                                                        </span>
                                                        <span className="date-text">{formatDate(primerAuxiliar.ultimoReajuste)}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="valor-actual">
                                                        ${formatMoney(valorActual)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="input-wrapper">
                                                        <span className="currency-symbol">$</span>
                                                        <input
                                                            type="number"
                                                            className={`valor-input ${hasChange ? 'modified' : ''}`}
                                                            placeholder={valorActual}
                                                            value={valorModificado || ''}
                                                            onChange={(e) => handleInputChange(key, e.target.value)}
                                                            min="0"
                                                            step="1000"
                                                        />
                                                        {hasChange && (
                                                            <button
                                                                className="clear-btn"
                                                                onClick={() => handleInputChange(key, null)}
                                                                title="Limpiar cambio"
                                                            >
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {hasChange && (
                                                        <div className={`diferencia ${diferencia >= 0 ? 'positive' : 'negative'}`}>
                                                            <span className="diff-amount">
                                                                {diferencia >= 0 ? '+' : ''}${formatMoney(Math.abs(diferencia))}
                                                            </span>
                                                            <span className="diff-percent">
                                                                ({diferencia >= 0 ? '+' : ''}{porcentaje}%)
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && pacienteData.auxiliares.map((auxiliar, idx) => (
                                                <tr key={`${key}-aux-${idx}`} className="auxiliar-detail-row">
                                                    <td></td>
                                                    <td colSpan="6">
                                                        <div className="auxiliar-detail">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                                <circle cx="12" cy="7" r="4" />
                                                            </svg>
                                                            <span className="auxiliar-name">{auxiliar.nombre}</span>
                                                            <span className="auxiliar-rut">{auxiliar.rut}</span>
                                                            <span className="auxiliar-acuerdo">Acuerdo #{auxiliar.idAcuerdo}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReajusteAuxiliar;
